import { useEffect, useMemo, useState } from "react";
import GlobalSVG from "../../utils/GlobalSVG";
import API from "../../utils/API";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ServiceCategoryBadges from "../../components/serviceCategoryBadges";

function OrdersOfServices() {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [step, setStep] = useState("");
  const [user, setUser] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [seenServices, setSeenServices] = useState(new Set());

  const [statusFilter, setStatusFilter] = useState("Todos");
  const [specialFilter, setSpecialFilter] = useState("none");

  const navigate = useNavigate();

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
    setUser(u);
    if (u && ["MECANICO", "ADMIN", "RECEPCAO", "BOSS"].includes(u.role)) setStep("1");
    if (u && ["COMPRADOR"].includes(u.role)) setStep("5");
    const stored = JSON.parse(localStorage.getItem("yc_seen_services") || "[]");
    setSeenServices(new Set(stored));
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("yc_usr_dt_ls")) { window.location.href = "/login"; return; }
    Swal.fire({ title: "Carregando...", showConfirmButton: false, allowOutsideClick: false, willOpen: () => Swal.showLoading() });

    API.getServices(API.cancelToken()).then((res) => {
      const ACCEPTED_STATUSES = new Set(["Aceito Pelo Cliente - OS Emitida", "AGUARDANDO PEÇA", "EM REPARO", "DISPONÍVEL PARA TESTE", "DISPONÍVEL PARA RETIRADA E PAGAMENTO", "Finalizado"]);
      let originData = res.data.map((item) => {
        const status = item.status || "";
        const expected = item.expected_delivery;
        return {
          id: Number(item.service_id),
          license_plate: item.license_plate || "",
          data: item.created_at,
          client: item.name || "",
          descricao: item.problem_description || "",
          status,
          readyToProcurement: item.ready_to_procurement,
          bossApprovalStatus: item.boss_approval_status,
          serviceCategories: item.service_categories,
          expected_delivery: item.expected_delivery,
          isExecucaoImediata: ACCEPTED_STATUSES.has(status) && (expected === null || expected === "0000-00-00"),
        };
      });
      const u = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
      if (u && u.role === "COMPRADOR") originData = originData.filter((i) => i.readyToProcurement === "true" && i.bossApprovalStatus === "approved");
      setData(originData);
      Swal.close();
    });
  }, []);

  function formatDate(dateString) {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "-";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  }

  const toggleSort = (key) => {
    setSortConfig((prev) => prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" });
  };

  const compareValues = (a, b, key, direction) => {
    if (key === "id") { const r = (a.id || 0) - (b.id || 0); return direction === "asc" ? r : -r; }
    if (key === "data") { const r = (new Date(a.data).getTime() || 0) - (new Date(b.data).getTime() || 0); return direction === "asc" ? r : -r; }
    const r = (a[key] || "").toString().toLowerCase().localeCompare((b[key] || "").toString().toLowerCase(), "pt-BR");
    return direction === "asc" ? r : -r;
  };

  const statusOptions = useMemo(() => {
    const unique = Array.from(new Set((data || []).map((d) => d.status.toUpperCase()).filter(Boolean)));
    unique.sort((a, b) => a.localeCompare(b, "pt-BR"));
    return ["Todos", ...unique];
  }, [data]);

  const sortedAndFiltered = useMemo(() => {
    let base = [...data];
    if (statusFilter !== "Todos") base = base.filter((row) => (row.status || "") === statusFilter);
    if (specialFilter === "aguardando") base = base.filter((row) => (row.status || "") === "Aguardando Checklist");
    else if (specialFilter === "em_teste") base = base.filter((row) => (row.status || "") === "DISPONÍVEL PARA TESTE");
    const q = searchQuery.trim().toLowerCase();
    const filtered = q ? base.filter((item) => [item.id, item.license_plate, item.client, item.descricao, item.status, formatDate(item.data)].map(String).some((v) => v.toLowerCase().includes(q))) : base;
    const exec = filtered.filter((i) => i.isExecucaoImediata);
    const others = filtered.filter((i) => !i.isExecucaoImediata);
    exec.sort((a, b) => compareValues(a, b, sortConfig.key, sortConfig.direction));
    others.sort((a, b) => compareValues(a, b, sortConfig.key, sortConfig.direction));
    return [...exec, ...others];
  }, [data, searchQuery, sortConfig, statusFilter, specialFilter]);

  const handleMarkSeen = (id) => {
    const updated = new Set(seenServices);
    updated.add(id);
    setSeenServices(updated);
    localStorage.setItem("yc_seen_services", JSON.stringify([...updated]));
  };

  const isMechanic = user?.role === "MECANICO";
  const isChief = user?.role === "BOSS";

  const toggleAguardando = () => { setSpecialFilter((v) => (v === "aguardando" ? "none" : "aguardando")); setStatusFilter("Todos"); };
  const toggleEmTeste = () => { setSpecialFilter((v) => (v === "em_teste" ? "none" : "em_teste")); setStatusFilter("Todos"); };

  const ORDER_VIEW_STATUSES = new Set(["Aceito Pelo Cliente - OS Emitida", "AGUARDANDO PEÇA", "EM REPARO", "DISPONÍVEL PARA TESTE", "DISPONÍVEL PARA RETIRADA E PAGAMENTO", "Finalizado"]);
  // Statuses that are still in early creation (vehicle/client/initial checklist)
  const EARLY_PHASE_STATUSES = new Set(["Aguardando Recepção", "Recepção em Andamento"]);
  // Mechanic lands on the entry checklist (step 3) so they can double-check the
  // reception's apontamentos, the owner's complaint, and media before the
  // mandatory checklist.
  const ROLE_DEFAULT_STEP = { MECANICO: "3", COMPRADOR: "5", RECEPCAO: "3" };
  const computeLink = (row) => {
    // COMPRADOR always goes to step 5 (orçamento) regardless of status
    if (user?.role === "COMPRADOR") return `/create/service?id=${row.id}&step=5`;
    if (ORDER_VIEW_STATUSES.has(row.status)) return `/order?id=${row.id}`;
    // If it's an early-phase service, use the role-based step
    if (EARLY_PHASE_STATUSES.has(row.status)) {
      const stepForRow = specialFilter === "aguardando" ? "3" : step;
      return `/create/service?id=${row.id}&step=${stepForRow}`;
    }
    // Use the role's natural step, fallback to 5
    const roleStep = ROLE_DEFAULT_STEP[user?.role] || "5";
    return `/create/service?id=${row.id}&step=${roleStep}`;
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toUpperCase();
    const map = {
      "AGUARDANDO CHECKLIST": "bg-yellow-100 text-yellow-800",
      "AGUARDANDO APROVAÇÃO DO CHEFE": "bg-amber-100 text-amber-800",
      "CHECKLIST REJEITADO": "bg-red-100 text-red-800",
      "AGUARDANDO ORÇAMENTO": "bg-orange-100 text-orange-800",
      "ORÇAMENTO ENVIADO": "bg-blue-100 text-blue-800",
      "ACEITO PELO CLIENTE - OS EMITIDA": "bg-green-100 text-green-800",
      "AGUARDANDO PEÇA": "bg-purple-100 text-purple-800",
      "EM REPARO": "bg-indigo-100 text-indigo-800",
      "DISPONÍVEL PARA TESTE": "bg-cyan-100 text-cyan-800",
      "DISPONÍVEL PARA RETIRADA E PAGAMENTO": "bg-emerald-100 text-emerald-800",
      "FINALIZADO": "bg-gray-100 text-gray-800",
    };
    const cls = map[s] || "bg-gray-100 text-gray-600";
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{status}</span>;
  };

  const renderSortIcon = (key) => {
    const isActive = sortConfig.key === key;
    return (
      <span className="inline-flex flex-col ml-1">
        <span className={isActive && sortConfig.direction === "asc" ? "text-[#389E0D]" : "text-gray-300"}>{GlobalSVG.caretUp()}</span>
        <span className={`${isActive && sortConfig.direction === "desc" ? "text-[#389E0D]" : "text-gray-300"} -mt-1`}>{GlobalSVG.caretDown()}</span>
      </span>
    );
  };

  const hasActiveFilter = specialFilter !== "none" || statusFilter !== "Todos";

  return (
    <div className="flex w-full h-full pt-8 px-20 pb-24 items-center gap-8 self-stretch max-md:px-4">
      <div className="flex w-full h-auto py-6 px-11 flex-col items-center gap-6 self-stretch bg-white rounded-lg shadow max-md:px-4">
        {/* Header */}
        <div className="flex w-full justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Serviços</h1>
            <p className="text-sm text-gray-500 mt-1">
              {sortedAndFiltered.length} serviço{sortedAndFiltered.length !== 1 ? "s" : ""}
              {hasActiveFilter ? " (filtrado)" : ""}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Pesquisar..."
                className="border border-gray-300 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent w-64"
              />
            </div>
            {user?.role !== "MECANICO" && (
              <button
                onClick={() => navigate("/create/service")}
                className="flex items-center gap-2 bg-[#389E0D] text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Nova OS
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex w-full items-center gap-3 flex-wrap">
          {isMechanic && (
            <button
              type="button"
              onClick={toggleAguardando}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all ${
                specialFilter === "aguardando"
                  ? "border-[#389E0D] bg-green-50 text-[#389E0D]"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              Aguardando Checklist
            </button>
          )}
          {isChief && (
            <button
              type="button"
              onClick={toggleEmTeste}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all ${
                specialFilter === "em_teste"
                  ? "border-[#389E0D] bg-green-50 text-[#389E0D]"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Veículos para teste
            </button>
          )}

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setSpecialFilter("none"); }}
              className="appearance-none pr-8 pl-3 py-1.5 border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent min-w-[220px] transition-all"
            >
              {statusOptions.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {hasActiveFilter && (
            <button
              type="button"
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => { setSpecialFilter("none"); setStatusFilter("Todos"); }}
            >
              Limpar filtros
            </button>
          )}
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => toggleSort("id")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex items-center gap-1">OS {renderSortIcon("id")}</div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Placa</th>
                <th onClick={() => toggleSort("data")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex items-center gap-1">Data {renderSortIcon("data")}</div>
                </th>
                <th onClick={() => toggleSort("client")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex items-center gap-1">Cliente {renderSortIcon("client")}</div>
                </th>
                <th onClick={() => toggleSort("descricao")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer min-w-[200px]">
                  <div className="flex items-center gap-1">Problema {renderSortIcon("descricao")}</div>
                </th>
                <th onClick={() => toggleSort("status")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                  <div className="flex items-center gap-1">Status {renderSortIcon("status")}</div>
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedAndFiltered.length > 0 ? (
                sortedAndFiltered.map((row) => {
                  const isSeen = seenServices.has(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={`transition-colors ${row.isExecucaoImediata ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-gray-50"}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {!isSeen && <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0"></span>}
                          <span className={`text-sm ${isSeen ? "text-gray-600" : "font-bold text-gray-900"}`}>#{row.id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {row.license_plate ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs font-mono font-semibold text-gray-700 tracking-wider">
                            {row.license_plate}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(row.data)}</td>
                      <td className="px-4 py-3">
                        <p className={`text-sm ${isSeen ? "text-gray-600" : "font-semibold text-gray-900"}`}>{row.client || "-"}</p>
                      </td>
                      <td className="px-4 py-3 min-w-[200px]">
                        <p className="text-sm text-gray-600 line-clamp-2">{row.descricao || "-"}</p>
                        <ServiceCategoryBadges
                          codes={row.serviceCategories}
                          size="sm"
                          showLabel={false}
                          className="mt-1"
                        />
                        {row.isExecucaoImediata && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                            Execução Imediata
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(row.status)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={computeLink(row)}
                          onClick={() => handleMarkSeen(row.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#389E0D] hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                          Ver
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400 text-sm">
                    Nenhuma ordem de serviço encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default OrdersOfServices;
